import React from "react";
import { Box, Paper, Typography, Chip, Divider } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface PolicyReviewCardProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
}

const getStatusChipColor = (value?: string) => {
    const v = (value || "").toLowerCase();
    if (v === "active" || v === "open") return "success" as const;
    if (v === "closed" || v === "inactive" || v === "cancelled") return "default" as const;
    if (v === "past due" || v === "overdue") return "warning" as const;
    return "default" as const;
};

const getItemValue = (items: any[], label: string) => {
    const found = (items || []).find((i) => (i?.label || "").toLowerCase() === label.toLowerCase());
    return found ? found.value : undefined;
};

const PolicyReviewCard: React.FC<PolicyReviewCardProps> = ({ card }) => {
    if (!card) return null;

    // Extract policy details from the first section
    const detailsSection = (card.sections || []).find((s: any) => (s?.heading || "").toLowerCase().includes("policy")) || card.sections?.[0];
    const premium = getItemValue(detailsSection?.items || [], "Premium");
    const dueAmount = getItemValue(detailsSection?.items || [], "Due Amount");
    const effective = getItemValue(detailsSection?.items || [], "Effective") || getItemValue(detailsSection?.items || [], "Effective Date");
    const expires = getItemValue(detailsSection?.items || [], "Expires") || getItemValue(detailsSection?.items || [], "Expires Date");
    const dueDate = getItemValue(detailsSection?.items || [], "Due Date");
    const status = getItemValue(detailsSection?.items || [], "Status");
    const policyNumber = getItemValue(detailsSection?.items || [], "Policy") || getItemValue(detailsSection?.items || [], "Policy Number");


    // Get title - use card title or default to "Policy Details - Auto"
    const title = card?.title;

    return (
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #e5e7eb",
                borderRadius: { xs: 1, md: 2 },
                p: { xs: 1, md: 1.5 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 14, md: 16 } }}>
                        {title}
                    </Typography>

                    <Typography sx={{ fontSize: { xs: 11, md: 13 }, color: "#374151", mt: 0.5 }}>
                        {card.subtitle}
                    </Typography>

                </Box>
                {status && (
                    <Chip
                        label={status}
                        color={getStatusChipColor(status)}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            borderRadius: 9999,
                        }}
                    />
                )}
            </Box>

            {/* Policy Details Section - Premium, Due Amount, Dates */}
            {(premium || dueAmount || effective || expires || dueDate) && (
                <Box sx={{ mt: 1.5 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: { xs: 1, md: 1.5 },
                            mt: 1,
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Premium</Typography>
                            <Typography sx={{ fontWeight: 700, color: "#1e68ff", fontSize: { xs: 14, md: 18 } }}>
                                {premium}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Due Amount</Typography>
                            <Typography sx={{ fontWeight: 700, color: "#f59e0b", fontSize: { xs: 14, md: 18 } }}>
                                {dueAmount}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Effective</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{effective}</Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Expires</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{expires}</Typography>
                        </Box>

                        {dueDate && (
                            <Box sx={{ gridColumn: { xs: "auto", sm: "1 / span 2" } }}>
                                <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Due Date</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {/* <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} /> */}
                                    <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{dueDate}</Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}

            {/* Other Sections */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 2 }}>
                {(card?.sections || []).map((section: any, sIdx: number) => {
                    // Check if this is the policy details section that we already rendered
                    const isPolicyDetailsSection = detailsSection && section === detailsSection;
                    const hasRemainingItems = isPolicyDetailsSection && (section?.items || []).some((item: any) => {
                        const label = (item?.label || "").toLowerCase();
                        return !["premium", "due amount", "effective", "effective date", "expires", "expires date", "due date", "status", "policy", "policy number"].includes(label);
                    });

                    // Skip rendering the policy details section if it has no remaining items
                    if (isPolicyDetailsSection && !hasRemainingItems) {
                        return null;
                    }

                    const heading = section?.heading || "";
                    const headingLower = heading.toLowerCase();
                    const isBilling = headingLower.includes("billing");
                    const isInvoices = headingLower.includes("invoice");
                    const isPayments = headingLower.includes("payment");
                    const isClaims = headingLower.includes("claim");

                    return (
                        <Box key={`pr_section_${sIdx}`}>
                            {(() => {
                                // Render Billing as a main heading
                                if (isBilling) {
                                    return (
                                        <Typography
                                            component="h2"
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: { xs: 14, md: 16 },
                                                //color: "#374151", 
                                                mb: "-9px",
                                                mt: isBilling ? 0.5 : 0
                                            }}
                                        >
                                            {heading}
                                        </Typography>
                                    );
                                }
                                // Render Invoices and Payments as subsection headings
                                if (isInvoices || isPayments || isClaims) {
                                    return (
                                        <Typography
                                            component="h3"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: 12, md: 14 },
                                                color: "#374151",
                                                mb: 1,
                                                mt: isInvoices ? 1 : 0
                                            }}
                                        >
                                            {heading}
                                        </Typography>
                                    );
                                }
                                // Regular section headings
                                return (
                                    <Typography
                                        sx={{ fontWeight: 700, fontSize: { xs: 11, md: 12 }, color: "#374151" }}
                                    >
                                        {heading}
                                    </Typography>
                                );
                            })()}
                            {!isClaims && !isBilling && !isInvoices && !isPayments && (
                                <Divider sx={{ mt: 0.75, mb: isInvoices || isPayments ? 0 : 0.75 }} />
                            )}

                            {(section?.items || []).length === 0 ? (
                                <Box sx={{ py: isBilling ? 0 : 1 }} />
                            ) : (
                                <Box sx={{ px: 0, py: 0.25 }}>
                                    {(() => {
                                        // Handle Invoices section with card-based layout
                                        if (isInvoices && !isClaims) {
                                            return (
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                    {(section?.items || []).map((item: any, iIdx: number) => {
                                                        // Parse invoice data
                                                        const invoiceDate = item.label?.replace("Invoice Date: ", "") || "";
                                                        const valueParts = item.value?.split(" | ") || [];
                                                        const amountStr = valueParts[0]?.replace("Amount: ", "") || "";
                                                        const dueDateStr = valueParts[1]?.replace("Due: ", "") || "";

                                                        // Format amount with $ prefix if it's a number
                                                        const amount = amountStr ? (amountStr.includes("$") ? amountStr : `$${amountStr}`) : "";

                                                        return (
                                                            <Paper
                                                                key={`invoice_card_${sIdx}_${iIdx}`}
                                                                elevation={0}
                                                                sx={{
                                                                    border: "1px solid #e5e7eb",
                                                                    borderRadius: 2,
                                                                    p: { xs: 1, md: 1.5 },
                                                                    bgcolor: "#ffffff",
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                        gap: { xs: 1, md: 1.5 },
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Premium</Typography>
                                                                        <Typography sx={{ fontWeight: 700, color: "#1e68ff", fontSize: { xs: 14, md: 18 } }}>
                                                                            {amount || "-"}
                                                                        </Typography>
                                                                    </Box>

                                                                    <Box>
                                                                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>InvoiceDate</Typography>
                                                                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{invoiceDate || "-"}</Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Due Date</Typography>
                                                                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{dueDateStr || "-"}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Paper>
                                                        );
                                                    })}
                                                </Box>
                                            );
                                        }
                                        // Handle Payments section with card-based layout
                                        if (isPayments && !isClaims) {
                                            return (
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                    {(section?.items || []).map((item: any, iIdx: number) => {
                                                        const paymentDate = item.label?.replace("Payment Date: ", "") || "";
                                                        const paidAmountRaw = item.value?.replace("Paid Amount: ", "") || "";
                                                        const amount = paidAmountRaw ? (paidAmountRaw.includes("$") ? paidAmountRaw : `$${paidAmountRaw}`) : "";

                                                        return (
                                                            <Paper
                                                                key={`payment_card_${sIdx}_${iIdx}`}
                                                                elevation={0}
                                                                sx={{
                                                                    border: "1px solid #e5e7eb",
                                                                    borderRadius: 2,
                                                                    p: { xs: 1, md: 1.5 },
                                                                    bgcolor: "#ffffff",
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: "grid",
                                                                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                        gap: { xs: 1, md: 1.5 },
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Paid Amount</Typography>
                                                                        <Typography sx={{ fontWeight: 700, color: "#1e68ff", fontSize: { xs: 14, md: 18 } }}>
                                                                            {amount || "-"}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>Payment Date</Typography>
                                                                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 } }}>{paymentDate || "-"}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Paper>
                                                        );
                                                    })}
                                                </Box>
                                            );
                                        }

                                        if (!isClaims) {
                                            const filteredItems = (section?.items || []).filter((item: any) => {
                                                // For policy details section, filter out items already displayed in the top section
                                                if (isPolicyDetailsSection) {
                                                    const label = (item?.label || "").toLowerCase();
                                                    return !["premium", "due amount", "effective", "effective date", "expires", "expires date", "due date", "status", "policy", "policy number"].includes(label);
                                                }
                                                return true;
                                            });

                                            return (
                                                <>
                                                    {filteredItems.map((item: any, iIdx: number) => {
                                                        const isStatus = (item?.label || "").toLowerCase() === "status";
                                                        const showChip = isStatus || ["open", "closed", "active", "inactive"].includes(
                                                            (item?.value || "").toLowerCase()
                                                        );
                                                        const isLast = iIdx === filteredItems.length - 1;
                                                        return (
                                                            <Box key={`pr_item_${sIdx}_${iIdx}`}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        alignItems: "center",
                                                                        py: { xs: 0.75, md: 1 },
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                                        {/* <FiberManualRecordIcon sx={{ fontSize: 10, color: "#9ca3af" }} /> */}
                                                                        <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: "#374151" }}>
                                                                            {item?.label}
                                                                        </Typography>
                                                                    </Box>

                                                                    {showChip ? (
                                                                        <Chip
                                                                            label={item?.value}
                                                                            size="small"
                                                                            color={getStatusChipColor(item?.value)}
                                                                            sx={{ fontWeight: 600, borderRadius: 9999 }}
                                                                        />
                                                                    ) : (
                                                                        <Typography sx={{ fontSize: { xs: 11, md: 12 }, color: "#111827" }}>
                                                                            {item?.value}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                {!isLast && <Divider />}
                                                            </Box>
                                                        );
                                                    })}
                                                </>
                                            );
                                        }

                                        // Build per-claim groups using Claim Number
                                        const items = section?.items || [];
                                        const claims: any[] = [];
                                        let current: any = null;
                                        let pending: any = {};
                                        let divertToPending = false; // once we see a Policy Number after a claim started, subsequent fields belong to the next claim
                                        items.forEach((raw: any) => {
                                            const label = (raw?.label || "").toLowerCase();
                                            const value = raw?.value;
                                            if (label === "claim number") {
                                                if (current) claims.push(current);
                                                current = { claimNumber: value, ...pending };
                                                pending = {};
                                                divertToPending = false; // we're now inside the new claim
                                            } else if (label === "status") {
                                                if (current && !divertToPending) current.status = value; else pending.status = value;
                                            } else if (label === "updated on") {
                                                if (current && !divertToPending) current.updatedOn = value; else pending.updatedOn = value;
                                            } else if (label === "claim initiated") {
                                                if (current && !divertToPending) current.claimInitiated = value; else pending.claimInitiated = value;
                                            } else if (label === "contact person") {
                                                const parts = String(value || "").split("|").map((s) => s.trim());
                                                const contact = { contact: parts[0] || "", phone: parts[1] || "", email: parts[2] || "" };
                                                if (current && !divertToPending) Object.assign(current, contact); else Object.assign(pending, contact);
                                            } else if (label === "policy number") {
                                                // Intentionally ignored for claims view; but if a policy number appears after a claim started,
                                                // it indicates the next claim group is beginning. Divert subsequent fields to pending.
                                                if (current) divertToPending = true;
                                            }
                                        });
                                        if (current) claims.push(current);

                                        return (
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                                                {claims.map((cl: any, cIdx: number) => (
                                                    <Paper
                                                        key={`claim_${cIdx}`}
                                                        elevation={0}
                                                        sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: { xs: 1, md: 1.5 }, bgcolor: "#ffffff" }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                            <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>{cl?.claimNumber}</Typography>
                                                            {!!cl?.status && (
                                                                <Chip
                                                                    label={cl.status}
                                                                    size="small"
                                                                    color={getStatusChipColor(cl.status)}
                                                                    sx={{ fontWeight: 600, borderRadius: 9999 }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Divider sx={{ mt: 0.75, mb: 1 }} />
                                                        <Box>
                                                            <Box
                                                                sx={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                    gap: { xs: 1, md: 1.5 },
                                                                    mt: 1,
                                                                }}
                                                            >
                                                                {[
                                                                    { label: "Claim Initiated", value: cl?.claimInitiated },
                                                                    { label: "Updated On", value: cl?.updatedOn },
                                                                    { label: "Contact", value: cl?.contact },
                                                                    { label: "Phone", value: cl?.phone },
                                                                    { label: "Email", value: cl?.email },
                                                                ].filter((row) => !!row.value).map((row, rIdx) => (
                                                                    <Box key={`claim_row_${cIdx}_${rIdx}`}>
                                                                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>{row.label}</Typography>
                                                                        <Typography sx={{ fontWeight: 600, fontSize: { xs: 12, md: 14 }, color: "#111827" }}>{row.value}</Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    </Paper>
                                                ))}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

export default PolicyReviewCard;

